import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Bus, ShieldCheck } from "lucide-react";
import { Seo } from "@/components/Seo";

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [cities, setCities] = useState([]);
  const [form, setForm] = useState({ name: "", business_name: "", phone: "", password: "", city: "", role: "driver" });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/cities").then((r) => setCities(r.data)).catch(() => {});
  }, []);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.password) {
      toast.error("Lengkapi nama, nomor WhatsApp, dan kata sandi");
      return;
    }
    setLoading(true);
    try {
      await register(form);
      toast.success("Pendaftaran berhasil! Menunggu verifikasi Admin.");
      navigate("/dashboard-driver");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4 py-10">
      <Seo title="Daftar Mitra Driver | Travele" description="Daftar sebagai mitra driver Travele dan tampilkan jadwal Anda di Digital Terminal." noindex />
      <div className="w-full max-w-md border border-border bg-card p-8">
        <Link to="/" className="mb-6 flex items-center gap-2">
          <div className="flex h-9 w-9 items-center justify-center rounded-md bg-primary text-primary-foreground">
            <Bus className="h-5 w-5" />
          </div>
          <span className="font-display text-xl font-black tracking-tighter">Travele</span>
        </Link>
        <h1 className="font-display text-2xl font-bold">Daftar Sebagai Driver</h1>
        <p className="mb-6 text-sm text-muted-foreground">
          Verifikasi minimal dilakukan lewat nomor WhatsApp oleh Admin.
        </p>
        <form onSubmit={submit} className="grid gap-4">
          <div className="grid gap-1.5">
            <Label>Nama Pemilik / Driver</Label>
            <Input data-testid="reg-name" value={form.name} onChange={set("name")} placeholder="Nama lengkap" />
          </div>
          <div className="grid gap-1.5">
            <Label>Nama Usaha Travel</Label>
            <Input data-testid="reg-business" value={form.business_name} onChange={set("business_name")} placeholder="cth. Travel Berkah Jaya" />
          </div>
          <div className="grid gap-1.5">
            <Label>Nomor WhatsApp</Label>
            <Input data-testid="reg-phone" value={form.phone} onChange={set("phone")} placeholder="08xxxx" />
          </div>
          <div className="grid gap-1.5">
            <Label>Kota Asal</Label>
            <Select value={form.city} onValueChange={(v) => setForm({ ...form, city: v })}>
              <SelectTrigger data-testid="reg-city"><SelectValue placeholder="Pilih kota" /></SelectTrigger>
              <SelectContent>{cities.map((c) => <SelectItem key={c.slug} value={c.name}>{c.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="grid gap-1.5">
            <Label>Kata Sandi</Label>
            <Input data-testid="reg-password" type="password" value={form.password} onChange={set("password")} placeholder="••••••" />
          </div>
          <div className="flex items-start gap-2 border border-border bg-background p-3 text-xs text-muted-foreground">
            <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
            Dengan mendaftar, Anda menyetujui bahwa setiap order dari Travele dikenakan komisi platform 20% dari nilai tiket yang Anda tetapkan.
          </div>
          <Button data-testid="reg-submit" type="submit" disabled={loading} className="bg-primary font-bold text-primary-foreground hover:bg-primary/90">
            {loading ? "Memproses..." : "Daftar"}
          </Button>
        </form>
        <p className="mt-4 text-center text-sm text-muted-foreground">
          Sudah punya akun? <Link to="/masuk" className="text-primary hover:underline">Masuk</Link>
        </p>
      </div>
    </div>
  );
}
